/**
 * Tema chiaro / scuro (Andrea 2026-10-08).
 *
 * I colori dell'app sono variabili CSS (vedi index.css e tailwind.config.ts):
 * qui si decide solo quale set attivare mettendo `data-theme="light"|"dark"`
 * su <html>. La scelta vive in localStorage ("fs-tema"); "sistema" segue
 * la preferenza del telefono. Lo script inline in index.html applica lo
 * stesso valore PRIMA del primo render, per non avere il flash scuro→chiaro.
 */

import { useSyncExternalStore } from "react";

export type Tema = "scuro" | "chiaro" | "sistema";
export type TemaEffettivo = "scuro" | "chiaro";

export const CHIAVE_TEMA = "fs-tema";

/** Colore della barra del browser/telefono, uguale allo sfondo dell'intestazione. */
const COLORE_BARRA: Record<TemaEffettivo, string> = { scuro: "#2A0F4E", chiaro: "#F1DF9E" };

const ascoltatori = new Set<() => void>();
let temaCorrente: Tema = leggiTema();

function leggiTema(): Tema {
  try {
    const v = localStorage.getItem(CHIAVE_TEMA);
    if (v === "chiaro" || v === "scuro" || v === "sistema") return v;
  } catch {
    /* localStorage non disponibile */
  }
  return "scuro";
}

function preferenzaSistema(): TemaEffettivo {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: light)").matches
    ? "chiaro"
    : "scuro";
}

export function temaEffettivo(t: Tema = temaCorrente): TemaEffettivo {
  return t === "sistema" ? preferenzaSistema() : t;
}

function applica() {
  const eff = temaEffettivo();
  const html = document.documentElement;
  html.dataset.theme = eff === "chiaro" ? "light" : "dark";
  html.style.colorScheme = eff === "chiaro" ? "light" : "dark";
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = COLORE_BARRA[eff];
  ascoltatori.forEach((f) => f());
}

export function impostaTema(t: Tema) {
  temaCorrente = t;
  try {
    localStorage.setItem(CHIAVE_TEMA, t);
  } catch {
    /* ignora */
  }
  applica();
}

export function temaScelto(): Tema {
  return temaCorrente;
}

// Se la scelta e' "sistema", segue i cambi del telefono (giorno/notte).
if (typeof window !== "undefined" && window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
    if (temaCorrente === "sistema") applica();
  });
  applica();
}

function iscrivi(f: () => void) {
  ascoltatori.add(f);
  return () => {
    ascoltatori.delete(f);
  };
}

/** La scelta dell'utente (scuro / chiaro / sistema). */
export function useTema(): Tema {
  return useSyncExternalStore(iscrivi, temaScelto, () => "scuro" as Tema);
}

/** Il tema davvero applicato in questo momento. */
export function useTemaEffettivo(): TemaEffettivo {
  return useSyncExternalStore(
    iscrivi,
    () => temaEffettivo(),
    () => "scuro" as TemaEffettivo,
  );
}
